import { Box, Center, Container, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { InviteUserRequestRole } from '@/api/generated/models/inviteUserRequestRole';
import {
  getListActionTokensQueryKey,
  useInviteUser,
  useListActionTokens,
  useResendInvite
} from '@/api/generated/users/users';
import { IconFigmaArrowRight } from '@/assets/icons';
import {
  CardSection,
  InfoBanner,
  ListPagination,
  MutationButton,
  PageHeading,
  SelectField,
  tableRowStyle,
  TextField,
  Toast
} from '@/components/common';
import { getInviteStatus, InviteStatusBadge } from '@/features/users/components/InviteStatusBadge';
import { formatDateTimeParts } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useOnlineStatus } from '@/utils/useOnlineStatus';

interface ActionToken {
  id: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  purpose: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
}

const schema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  role: z.string().min(1)
});

type FormValues = z.infer<typeof schema>;

const initials = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const cellStyle = { color: 'var(--color-text-secondary)' };

export const InviteColleaguePage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  // staleTime: 0 — invitee prihvaća pozivnicu iz posve druge sesije, bez
  // signala natrag ovoj stranici (isti razlog kao Partner Linking).
  const { data, isLoading } = useListActionTokens(
    { purpose: 'INVITE' },
    { query: { staleTime: 0 } }
  );
  const invites = (data as ActionToken[] | undefined) ?? [];
  const sorted = useMemo(
    () =>
      [...invites].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [invites]
  );
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems, offset } =
    useLocalPagination(sorted);

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { email: '', firstName: '', lastName: '', role: 'USER' }
  });

  const inviteMutation = useInviteUser({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        form.reset();
        setPage(1);
        queryClient.invalidateQueries({
          queryKey: getListActionTokensQueryKey({ purpose: 'INVITE' })
        });
      }
    }
  });

  const [resendFeedback, setResendFeedback] = useState<{
    email: string;
    tone: 'success' | 'danger';
  } | null>(null);

  const resendMutation = useResendInvite({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: getListActionTokensQueryKey({ purpose: 'INVITE' })
        });
        const email = sorted.find((i) => i.userId === variables.id)?.userEmail ?? '';
        setResendFeedback({ email, tone: 'success' });
      },
      onError: (_error, variables) => {
        const email = sorted.find((i) => i.userId === variables.id)?.userEmail ?? '';
        setResendFeedback({ email, tone: 'danger' });
      }
    }
  });

  const handleSubmit = (values: FormValues) => {
    inviteMutation.mutate({
      data: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role as InviteUserRequestRole
      }
    });
  };

  const handleResend = (item: ActionToken) => {
    setResendFeedback(null);
    resendMutation.mutate({ id: item.userId });
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <PageHeading title={t('users.invite.title')} subtitle={t('users.invite.subtitle')} />

        <CardSection title={t('users.invite.newInvite')}>
          <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
            <Group align="flex-end" wrap="wrap" gap={12} p={18} pb={34}>
              <Box w={{ base: '100%', sm: 160 }}>
                <TextField
                  label={t('users.invite.firstName')}
                  required
                  {...form.getInputProps('firstName')}
                />
              </Box>
              <Box w={{ base: '100%', sm: 160 }}>
                <TextField
                  label={t('users.invite.lastName')}
                  required
                  {...form.getInputProps('lastName')}
                />
              </Box>
              <Box w={{ base: '100%', sm: 220 }} style={{ flex: '1 1 220px' }}>
                <TextField
                  label={t('users.invite.email')}
                  placeholder={t('users.invite.emailPlaceholder')}
                  required
                  {...form.getInputProps('email')}
                />
              </Box>
              <Box w={{ base: '100%', sm: 180 }}>
                <SelectField
                  label={t('users.invite.role')}
                  required
                  allowDeselect={false}
                  data={[
                    { value: 'USER', label: t('users.invite.roleUser') },
                    { value: 'ORGANISATION_ADMIN', label: t('users.invite.roleAdmin') }
                  ]}
                  {...form.getInputProps('role')}
                />
              </Box>
              <MutationButton
                type="submit"
                w={{ base: '100%', sm: 'auto' }}
                rightSection={<IconFigmaArrowRight size={15} />}
                isPending={inviteMutation.isPending}
                isPaused={inviteMutation.isPaused}
              >
                {t('users.invite.send')}
              </MutationButton>
            </Group>
            {inviteMutation.isError && (
              <Text px={18} pb={14} fz={13} style={{ color: 'var(--color-status-danger)' }}>
                {t('users.invite.sendError')}
              </Text>
            )}
          </form>
        </CardSection>

        <CardSection title={t('users.invite.sentInvites')}>
          {resendFeedback && (
            <Box p={16}>
              <Toast tone={resendFeedback.tone}>
                {resendFeedback.tone === 'success'
                  ? t('users.invite.resendSuccess', { email: resendFeedback.email })
                  : t('users.invite.resendError', { email: resendFeedback.email })}
              </Toast>
            </Box>
          )}

          {!isLoading && sorted.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.invite.emptyState')}
              </Text>
            </Center>
          )}

          {sorted.length > 0 && (
            <>
              <Box visibleFrom="sm">
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {[
                    ['users.invite.colInvited', 190],
                    ['users.invite.colRole', 130],
                    ['users.invite.colSent', 130],
                    ['users.invite.colExpires', 130],
                    ['users.invite.colStatus', 130]
                  ].map(([key, width]) => (
                    <Text
                      key={key as string}
                      fz={10}
                      fw={600}
                      w={width}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t(key as string)}
                    </Text>
                  ))}
                  <Text
                    fz={10}
                    fw={600}
                    w={90}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {t('users.invite.colActions')}
                  </Text>
                </div>

                {pageItems.map((item) => {
                  const status = getInviteStatus(item.expiresAt, item.usedAt);
                  const sent = formatDateTimeParts(item.createdAt);
                  const expires = formatDateTimeParts(item.expiresAt);

                  return (
                    <div
                      key={item.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Group gap={10} wrap="nowrap" w={190}>
                        <Center
                          w={28}
                          h={28}
                          style={{
                            borderRadius: 999,
                            backgroundColor: 'var(--color-bg-surface-2)',
                            border: '1px solid var(--color-border-default)',
                            flexShrink: 0
                          }}
                        >
                          <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                            {initials(item.userFirstName, item.userLastName)}
                          </Text>
                        </Center>
                        <Stack gap={0}>
                          <Text fz={12.5} style={{ color: 'var(--color-text-primary)' }}>
                            {item.userFirstName} {item.userLastName}
                          </Text>
                          <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                            {item.userEmail}
                          </Text>
                        </Stack>
                      </Group>

                      <div style={{ width: 130 }}>
                        <Group
                          gap={4}
                          wrap="nowrap"
                          px={12}
                          py={4}
                          style={{
                            borderRadius: 999,
                            width: 'fit-content',
                            backgroundColor: 'var(--color-role-user-soft)',
                            color: 'var(--color-role-user)'
                          }}
                        >
                          <Text fz={11} fw={600} style={{ color: 'inherit' }}>
                            {t('users.invite.roleUser')}
                          </Text>
                        </Group>
                      </div>

                      <Stack gap={0} w={130}>
                        <Text fz={10.5} style={cellStyle}>
                          {sent.date}
                        </Text>
                        <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                          {sent.time}
                        </Text>
                      </Stack>

                      <Stack gap={0} w={130}>
                        <Text fz={10.5} style={cellStyle}>
                          {expires.date}
                        </Text>
                        <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                          {expires.time}
                        </Text>
                      </Stack>

                      <div style={{ width: 130 }}>
                        <InviteStatusBadge status={status} />
                      </div>

                      <Group w={90} justify="flex-end">
                        {status === 'expired' && (
                          <UnstyledButton
                            onClick={() => handleResend(item)}
                            disabled={
                              !isOnline ||
                              (resendMutation.isPending &&
                                resendMutation.variables?.id === item.userId)
                            }
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            <Text fz={12} fw={600} style={{ color: 'inherit' }}>
                              {t('users.invite.resend')}
                            </Text>
                          </UnstyledButton>
                        )}
                      </Group>
                    </div>
                  );
                })}
              </Box>

              <Stack hiddenFrom="sm" gap={12} p={16}>
                {pageItems.map((item) => {
                  const status = getInviteStatus(item.expiresAt, item.usedAt);
                  const sent = formatDateTimeParts(item.createdAt);
                  const expires = formatDateTimeParts(item.expiresAt);

                  return (
                    <Stack
                      key={item.id}
                      gap={12}
                      p={16}
                      style={{
                        borderRadius: 12,
                        border: '1px solid var(--color-border-soft)',
                        backgroundColor: 'var(--color-bg-surface)'
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap={8} wrap="nowrap">
                          <Center
                            w={28}
                            h={28}
                            style={{
                              borderRadius: 999,
                              backgroundColor: 'var(--color-bg-surface-2)',
                              border: '1px solid var(--color-border-default)',
                              flexShrink: 0
                            }}
                          >
                            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                              {initials(item.userFirstName, item.userLastName)}
                            </Text>
                          </Center>
                          <Stack gap={0}>
                            <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                              {item.userFirstName} {item.userLastName}
                            </Text>
                            <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                              {item.userEmail}
                            </Text>
                          </Stack>
                        </Group>

                        {status === 'expired' && (
                          <UnstyledButton
                            onClick={() => handleResend(item)}
                            disabled={
                              !isOnline ||
                              (resendMutation.isPending &&
                                resendMutation.variables?.id === item.userId)
                            }
                            style={{ color: 'var(--color-text-primary)', flexShrink: 0 }}
                          >
                            <Text fz={12} fw={600} style={{ color: 'inherit' }}>
                              {t('users.invite.resend')}
                            </Text>
                          </UnstyledButton>
                        )}
                      </Group>

                      <Box
                        style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }}
                        w="100%"
                      />

                      <Stack gap={8}>
                        <Group justify="space-between">
                          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                            {t('users.invite.colSent')}:
                          </Text>
                          <Text fz={11} style={cellStyle}>
                            {sent.date} {sent.time}
                          </Text>
                        </Group>
                        <Group justify="space-between">
                          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                            {t('users.invite.colExpires')}:
                          </Text>
                          <Text fz={11} style={cellStyle}>
                            {expires.date} {expires.time}
                          </Text>
                        </Group>
                        <Group gap={8}>
                          <Group
                            gap={4}
                            wrap="nowrap"
                            px={12}
                            py={4}
                            style={{
                              borderRadius: 999,
                              width: 'fit-content',
                              backgroundColor: 'var(--color-role-user-soft)',
                              color: 'var(--color-role-user)'
                            }}
                          >
                            <Text fz={11} fw={600} style={{ color: 'inherit' }}>
                              {t('users.invite.roleUser')}
                            </Text>
                          </Group>
                          <InviteStatusBadge status={status} />
                        </Group>
                      </Stack>
                    </Stack>
                  );
                })}
                <ListPagination
                  label={t('users.invite.showing', {
                    from: sorted.length === 0 ? 0 : offset + 1,
                    to: offset + pageItems.length,
                    total: sorted.length
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  bordered={false}
                />
              </Stack>

              <Box visibleFrom="sm">
                <ListPagination
                  label={t('users.invite.showing', {
                    from: sorted.length === 0 ? 0 : offset + 1,
                    to: offset + pageItems.length,
                    total: sorted.length
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </Box>
            </>
          )}
        </CardSection>

        <InfoBanner message={t('users.invite.infoBanner')} />
      </Stack>
    </Container>
  );
};
